import { isSameDay } from 'date-fns'
import { StrictMode, type VFC } from 'react'
import { ensureValidDate, ensureValidLocale } from '@opallabs/lambda'
import { screenreaderOnly } from '../utils'
import { type DateRangeProps } from './types'

export { type DateRangeProps }

/**
 * @private Only exposed for internal tests
 */
export const baseFormatterOptions: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
}

/**
 * Localized presentation of a single date or a date range.
 *
 * @note Don’t rely on the exact text output--it can change at any time, and
 * varies by locale.
 * @note When testing another component that renders `<DateRange>` you should
 * structure the `it` like
 *     ```jsx
 *     render(
 *       <>
 *         <div data-testid="actual">
 *           <YourComponent props={...}>
 *         </div>
 *         <div data-testid="daterange-reference">
 *           <DateRange endDate={endDate} startDate={startDate} />
 *         </div>
 *       </>
 *     )
 *     const expected = screen.getByTestId('daterange-reference').querySelector('[data-testid=daterange-range]')?.textContent || 'never'
 *     // You can replace the `actual` wrapper `data-testid` with a
 *     // `data-testid` that's within your component, or change this
 *     // `getByTestId` to a `getByRole` matching something in your component
 *     const actual = screen.getByTestId('actual').querySelector('[data-testid=daterange-range]')
 *     expect(actual).toHaveTextContent(expected, { normalizeWhitespace: false })
 *     ```
 * This is necessary because you can’t predict the exact output of
 * `<DateRange>`, so you have to render it to determine the output; but it’s bad
 * practice to call `render` multiple times within one test, so the `render`
 * call for your component needs to include an explicit `<DateRange>` with the
 * same props. (It may seem tautological to compare two `<DateRange>` instances,
 * but it validates that your component rendered `<DateRange>` with the props
 * you intended.) The test ids (`data-testid` attributes) differentiate your
 * component’s instance from the explicitly-rendered instance.
 */
export const DateRange: VFC<DateRangeProps> = ({
  className,
  endDate,
  forceRangeDisplay,
  includeDayName = false,
  locale,
  startDate,
}) => {
  ensureValidDate(startDate, 'startDate')
  if (endDate) ensureValidDate(startDate, 'endDate')
  if (locale) ensureValidLocale(locale)

  const formatterOptions = Object.assign({}, baseFormatterOptions)
  if (includeDayName) formatterOptions.weekday = 'short'

  const formatter = new Intl.DateTimeFormat(locale, formatterOptions)

  if ((!endDate || isSameDay(startDate, endDate)) && !forceRangeDisplay) {
    return (
      <StrictMode>
        <time
          dateTime={startDate.toISOString()}
          data-testid="daterange-range"
          className={className}
        >
          {formatter.format(startDate)}
        </time>
      </StrictMode>
    )
  }

  const rangeEnd = endDate ?? startDate
  // In my testing all locales appear to include a separator, so the fallback
  // may not ever be used. (I tried `ar-EG`, `da-DK`, `en-US`, `es-MX`, `ja`,
  // and `mn-MN`.)
  const separator =
    formatter
      .formatRangeToParts(startDate, rangeEnd)
      .find((part) => part.type === 'literal' && part.source === 'shared')
      ?.value || ' — '
  return (
    <StrictMode>
      <>
        <span aria-hidden data-testid="daterange-range" className={className}>
          {formatter.formatRange(startDate, rangeEnd)}
        </span>
        <span css={screenreaderOnly}>
          <time
            dateTime={startDate.toISOString()}
            data-testid="daterange-start"
          >
            {formatter.format(startDate)}
          </time>
          {separator}
          <time dateTime={rangeEnd.toISOString()} data-testid="daterange-end">
            {formatter.format(rangeEnd)}
          </time>
        </span>
      </>
    </StrictMode>
  )
}
DateRange.displayName = 'Lapidary: DateRange'
